import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

export default function Footer() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    if (!supabase) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setIsLoggedIn(!!user);
    } catch (error) {
      setIsLoggedIn(false);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Top CTA Bar */}
      <div className="bg-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h3 className="text-xl font-semibold mb-1">Ready to place a carton-based order?</h3>
              <p className="text-gray-400 text-sm">Browse our wholesale outdoor shoes collection</p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/products"
                className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse Products
              </Link>
              <Link
                to="/contact"
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Middle Columns - Desktop */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden md:grid md:grid-cols-4 gap-8">
            {/* Column A: About */}
            <div>
              <h4 className="text-lg font-semibold mb-4">About</h4>
              <div className="mb-4">
                <div className="text-2xl font-bold mb-3" style={{ fontFamily: '"Pacifico", serif' }}>
                  logo
                </div>
                <p className="text-gray-400 text-sm leading-relaxed mb-3">
                  GlobalShoesWholesale.com is a B2B platform focusing on outdoor shoes wholesale, carton-based orders only.
                </p>
                <p className="text-gray-400 text-xs italic">
                  For retailers and distributors only. We do not sell single pairs to end consumers.
                </p>
              </div>
            </div>

            {/* Column B: Products */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Products</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/products" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    All Products
                  </Link>
                </li>
                <li>
                  <Link to="/brands" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Brands
                  </Link>
                </li>
                <li>
                  <Link to="/categories" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Categories
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column C: Support & Legal */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Support &amp; Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/offline-payment" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Offline Payment Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column D: Account & Language */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Account</h4>
              <ul className="space-y-2 mb-6">
                {isLoggedIn ? (
                  <>
                    <li>
                      <Link to="/account/orders" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                        My Orders
                      </Link>
                    </li>
                    <li>
                      <Link to="/cart" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                        My Cart
                      </Link>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <Link to="/login" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                        Login
                      </Link>
                    </li>
                    <li>
                      <Link to="/register" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                        Register
                      </Link>
                    </li>
                  </>
                )}
              </ul>

              <h4 className="text-lg font-semibold mb-4">Language</h4>
              <div className="flex gap-2">
                <button className="px-3 py-1 bg-teal-600 text-white rounded text-sm font-medium cursor-pointer">
                  EN
                </button>
                <button className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm font-medium transition-colors cursor-pointer">
                  中文
                </button>
              </div>
            </div>
          </div>

          {/* Middle Columns - Mobile Accordion */}
          <div className="md:hidden space-y-4">
            {/* About Section */}
            <div className="border-b border-gray-800 pb-4">
              <button
                onClick={() => toggleSection('about')}
                className="w-full flex items-center justify-between text-left cursor-pointer"
              >
                <h4 className="text-lg font-semibold">About</h4>
                <i className={`ri-arrow-${openSection === 'about' ? 'up' : 'down'}-s-line text-xl`}></i>
              </button>
              {openSection === 'about' && (
                <div className="mt-4">
                  <div className="text-2xl font-bold mb-3" style={{ fontFamily: '"Pacifico", serif' }}>
                    logo
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-3">
                    GlobalShoesWholesale.com is a B2B platform focusing on outdoor shoes wholesale, carton-based orders only.
                  </p>
                  <p className="text-gray-400 text-xs italic">
                    For retailers and distributors only. We do not sell single pairs to end consumers.
                  </p>
                </div>
              )}
            </div>

            {/* Products Section */}
            <div className="border-b border-gray-800 pb-4">
              <button
                onClick={() => toggleSection('products')}
                className="w-full flex items-center justify-between text-left cursor-pointer"
              >
                <h4 className="text-lg font-semibold">Products</h4>
                <i className={`ri-arrow-${openSection === 'products' ? 'up' : 'down'}-s-line text-xl`}></i>
              </button>
              {openSection === 'products' && (
                <ul className="mt-4 space-y-2">
                  <li>
                    <Link to="/products" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                      All Products
                    </Link>
                  </li>
                  <li>
                    <Link to="/brands" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                      Brands
                    </Link>
                  </li>
                  <li>
                    <Link to="/categories" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                      Categories
                    </Link>
                  </li>
                </ul>
              )}
            </div>

            {/* Support Section */}
            <div className="border-b border-gray-800 pb-4">
              <button
                onClick={() => toggleSection('support')}
                className="w-full flex items-center justify-between text-left cursor-pointer"
              >
                <h4 className="text-lg font-semibold">Support &amp; Legal</h4>
                <i className={`ri-arrow-${openSection === 'support' ? 'up' : 'down'}-s-line text-xl`}></i>
              </button>
              {openSection === 'support' && (
                <ul className="mt-4 space-y-2">
                  <li>
                    <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                      Contact Us
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link to="/offline-payment" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                      Offline Payment Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                      Terms of Service
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
              )}
            </div>

            {/* Account Section */}
            <div className="border-b border-gray-800 pb-4">
              <button
                onClick={() => toggleSection('account')}
                className="w-full flex items-center justify-between text-left cursor-pointer"
              >
                <h4 className="text-lg font-semibold">Account &amp; Language</h4>
                <i className={`ri-arrow-${openSection === 'account' ? 'up' : 'down'}-s-line text-xl`}></i>
              </button>
              {openSection === 'account' && (
                <div className="mt-4">
                  <ul className="space-y-2 mb-6">
                    {isLoggedIn ? (
                      <>
                        <li>
                          <Link to="/account/orders" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                            My Orders
                          </Link>
                        </li>
                        <li>
                          <Link to="/cart" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                            My Cart
                          </Link>
                        </li>
                      </>
                    ) : (
                      <>
                        <li>
                          <Link to="/login" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                            Login
                          </Link>
                        </li>
                        <li>
                          <Link to="/register" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                            Register
                          </Link>
                        </li>
                      </>
                    )}
                  </ul>

                  <h5 className="text-sm font-semibold mb-3">Language</h5>
                  <div className="flex gap-2">
                    <button className="px-3 py-1 bg-teal-600 text-white rounded text-sm font-medium cursor-pointer">
                      EN
                    </button>
                    <button className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm font-medium transition-colors cursor-pointer">
                      中文
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar: Copyright & Payment Disclaimer */}
      <div className="bg-gray-950 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {/* Offline Payment Disclaimer - CRITICAL SAFETY NOTICE */}
            <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <i className="ri-alert-line text-yellow-500 text-xl flex-shrink-0 mt-0.5"></i>
                <div>
                  <p className="text-yellow-200 text-sm font-semibold mb-1">Important: Offline Payment Only</p>
                  <p className="text-yellow-300/90 text-xs leading-relaxed">
                    This website does not support any online payment. All payments are handled offline via bank transfer or agreed methods. Orders will be manually marked as paid by our admin after payment is confirmed.
                  </p>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center text-gray-400 text-sm">
              <p>© {currentYear} GlobalShoesWholesale.com. All rights reserved.</p>
            </div>

            {/* Readdy Link */}
            <div className="text-center">
              <a
                href="https://readdy.ai/?origin=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-400 text-xs transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
